import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { useGetPatients } from '../../hooks/patientsAPI';
import {
  ExistingPatient,
  PatientSearchProperties,
  PatientSortColumn,
  SearchPatientsParams,
  SortOrder,
} from '../../types';
import {
  DataTable,
  Pagination,
  TableSkeleton,
  PaginationState,
} from '@/components';
import { SortingState, OnChangeFn } from '@tanstack/react-table';
import { useDebounce } from '@/hooks';
import { EditPatientModal } from '../EditPatientModal';
import { useListProfiles } from '../../../profiles/hooks/profilesAPI';
import { SearchFilterHeader } from './SearchFilterHeader';
import { createColumns } from './tableColumns';
import PatientRequestsAccordion from './PatientRequestsAccordion';

// Empty State Component
const EmptyStateComponent = () => (
  <div className="flex items-center justify-center py-12">
    <div className="text-center">
      <img
        src="/suggested-providers.png"
        alt="No patients found"
        className="mx-auto mb-8 max-w-md"
      />
      <h2 className="text-2xl font-bold">No Patients Found</h2>
      <p className="text-gray-600">Try adjusting your search or filters.</p>
    </div>
  </div>
);

const DEFAULT_SORT: Array<{ column: PatientSortColumn; order: SortOrder }> = [
  { column: 'updatedAt', order: 'desc' },
];

export const PatientsList = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 10,
  });
  const [editingPatient, setEditingPatient] = useState<ExistingPatient | null>(
    null,
  );

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'updatedAt', desc: true },
  ]);
  const [search, setSearch] = useState<PatientSearchProperties>({
    terms: [],
    dateOfBirth: undefined,
    statuses: [],
    organizationProfileIds: undefined,
    modifiedFrom: undefined,
    modifiedTo: undefined,
  });
  const debouncedSearchTerm = useDebounce(search.terms[0] || '', 300);

  // Fetch profiles to determine if all are selected
  const { data: profilesData } = useListProfiles({
    assignedProfilesOnly: true,
    pagination: {
      offset: 0,
      limit: 100,
    },
  });

  const totalProfilesCount = profilesData?.totalCount || 0;
  const [hasInitializedProfiles, setHasInitializedProfiles] = useState(false);

  // Initialize with all profiles selected only once when profiles are first loaded
  useEffect(() => {
    if (
      !hasInitializedProfiles &&
      profilesData?.data &&
      profilesData.data.length > 0 &&
      search.organizationProfileIds === undefined
    ) {
      setHasInitializedProfiles(true);
      setSearch((prev) => ({
        ...prev,
        organizationProfileIds: profilesData.data.map((profile) => profile.id),
      }));
    }
  }, [
    hasInitializedProfiles,
    profilesData?.data,
    search.organizationProfileIds,
  ]);

  // undefined if all selected or not set, otherwise the array
  const apiProfileIds = useMemo(() => {
    if (
      search.organizationProfileIds === undefined ||
      (totalProfilesCount > 0 &&
        search.organizationProfileIds.length === totalProfilesCount)
    ) {
      return undefined;
    }
    return search.organizationProfileIds;
  }, [search.organizationProfileIds, totalProfilesCount]);

  // Convert TanStack sorting state to API format
  const sortColumns = useMemo(() => {
    if (sorting.length === 0) return DEFAULT_SORT;

    const order = (desc: boolean) =>
      desc ? ('desc' as const) : ('asc' as const);

    return sorting.map((sort) => ({
      column: sort.id as PatientSortColumn,
      order: order(sort.desc),
    }));
  }, [sorting]);

  const searchParams: SearchPatientsParams['search'] = {
    terms: debouncedSearchTerm ? [debouncedSearchTerm] : [],
    ...(search.dateOfBirth && {
      dateOfBirth: search.dateOfBirth,
    }),
    ...(search.statuses.length > 0 && {
      statuses: search.statuses,
    }),
    organizationProfileIds: apiProfileIds,
    ...(search.modifiedFrom && {
      modifiedFrom: format(search.modifiedFrom, 'yyyy-MM-dd'),
    }),
    ...(search.modifiedTo && {
      modifiedTo: format(search.modifiedTo, 'yyyy-MM-dd'),
    }),
  };

  const { data: patientsData, isLoading } = useGetPatients({
    search: searchParams,
    pagination: {
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    },
    sortColumns,
    includeRequests: true,
  });

  const handleEditPatient = (patient: ExistingPatient) => {
    setEditingPatient(patient);
  };

  const handleCloseEditModal = () => {
    setEditingPatient(null);
  };

  const handleSearchChange = (newSearch: PatientSearchProperties) => {
    // Reset to first page when search terms change
    if (newSearch.terms[0] !== search.terms[0]) {
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
    setSearch(newSearch);
  };

  const columns = createColumns(search, setSearch, handleEditPatient);

  return (
    <div className="min-h-screen bg-gray-50">
      <SearchFilterHeader
        search={search}
        handleSearchChange={handleSearchChange}
      />
      {isLoading ? (
        <div className="px-6 pb-6">
          <TableSkeleton rows={5} columns={6} />
        </div>
      ) : (
        <div className="px-6 pb-6">
          <DataTable
            columns={columns}
            data={patientsData?.patients || []}
            sorting={sorting}
            onSortingChange={setSorting as OnChangeFn<SortingState>}
            emptyState={<EmptyStateComponent />}
            accordionContent={(patient: ExistingPatient) => (
              <PatientRequestsAccordion patient={patient} />
            )}
          />
          <Pagination
            pagination={pagination}
            setPagination={setPagination}
            totalCount={patientsData?.totalCount ?? 0}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}
      {editingPatient && (
        <EditPatientModal
          patient={editingPatient}
          isOpen={!!editingPatient}
          onClose={handleCloseEditModal}
        />
      )}
    </div>
  );
};
